import React, { useState } from 'react';
import { Dialog, TextField, Typography, Alert, Button, Box, Stack } from '@mui/material';

interface RequiredParametersModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (parameters: Record<string, string>) => void;
  requiredParameters: string[];
}

const RequiredParametersModal: React.FC<RequiredParametersModalProps> = ({
  open,
  onClose,
  onSubmit,
  requiredParameters
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (param: string, value: string) => {
    setFormData(prev => ({ ...prev, [param]: value }));
    if (errors[param]) {
      setErrors(prev => ({ ...prev, [param]: '' }));
    }
  };

  const handleOk = () => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    requiredParameters.forEach(param => {
      if (!formData[param]?.trim()) {
        newErrors[param] = `Please enter ${param}`;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Required Parameters</Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Please provide values for all required parameters.
        </Alert>

        <Stack spacing={2} sx={{ mb: 3 }}>
          {requiredParameters.map((param) => (
            <TextField
              key={param}
              label={param}
              value={formData[param] || ''}
              onChange={(e) => handleInputChange(param, e.target.value)}
              placeholder={`Enter ${param}`}
              error={!!errors[param]}
              helperText={errors[param]}
              fullWidth
              required
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleOk}>
            Submit
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default RequiredParametersModal;